import { useTeamState } from "@/modules/team/states";
import ZAvatarGradient from "@/shared/components/avatarGradient";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@nextui-org/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ArrowLeft, Edit, FileText, TextCursorInput, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteQuestionnaire, getQuestionnaires, renameQuestionnaire, type Questionnaire } from "../../requests/questionnaire";
import { useQuestionnaireWizardStore } from "../../states/questionnaire-wizard";
import CreateQuestionnaireModal from "./CreateQuestionnaireModal";
import QuestionnaireConfirmationModal from "./QuestionnaireConfirmationModal";
import QuestionnaireRenameModal from "./QuestionnaireRenameModal";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const QuestionnairesList = () => {
  const navigate = useNavigate();
  const { openWizard } = useQuestionnaireWizardStore();
  const [loading, setLoading] = useState(false);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState<Questionnaire | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [questionnaireToRename, setQuestionnaireToRename] = useState<Questionnaire | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameErrorMessage, setRenameErrorMessage] = useState("");

  const { team } = useTeamState();

  useEffect(() => {
    void fetchQuestionnaires();
  }, []);

  // Add event listener for Create New button from layout
  useEffect(() => {
    const handleCreateQuestionnaireModal = () => {
      setIsCreateModalOpen(true);
    };

    window.addEventListener('openCreateQuestionnaireModal', handleCreateQuestionnaireModal);

    return () => {
      window.removeEventListener('openCreateQuestionnaireModal', handleCreateQuestionnaireModal);
    };
  }, []);



  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const [error, response] = await parsePromise(getQuestionnaires());
      if (error) {
        addNotification({
          type: "error",
          title: "Failed to fetch questionnaires",
          message: error?.message || "Something went wrong. Please try again later!",
        });
        return;
      }

      if (response?.success && response?.questionnaires) {
        setQuestionnaires(response?.questionnaires);
      }
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
      addNotification({
        type: "error",
        title: "Failed to fetch questionnaires",
        message: "Something went wrong. Please try again later!",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (memberId: string) => {
    const member = team.find((member) => member.member_id === memberId);
    return member?.name || "Unknown";
  };

  const formatLastUpdated = (dateString: string) => {
    if (dayjs(dateString).isValid()) {
      const parsedDate = dayjs.utc(dateString);
      const localDate = parsedDate.local();
      return localDate.fromNow();
    }
    return "Unknown";
  };

  const handleDeleteClick = (questionnaire: Questionnaire) => {
    setQuestionnaireToDelete(questionnaire);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionnaireToDelete) return;

    setIsDeleting(true);
    try {
      const [error, response] = await parsePromise(deleteQuestionnaire(questionnaireToDelete.id));
      if (error) {
        addNotification({
          type: "error",
          message: "An unexpected error occurred. Please try again later.",
        });
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: `"${questionnaireToDelete.questionnaire_name}" deleted successfully.`,
        });

        await fetchQuestionnaires();
        setIsDeleteModalOpen(false);
        setQuestionnaireToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting questionnaire:", error);
      addNotification({
        type: "error",
        title: "Failed to delete questionnaire",
        message: "Something went wrong. Please try again later!",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setQuestionnaireToDelete(null);
    }
  };

  const handleRenameClick = (questionnaire: Questionnaire) => {
    setQuestionnaireToRename(questionnaire);
    setRenameErrorMessage("");
    setIsRenameModalOpen(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!questionnaireToRename) return;

    setIsRenaming(true);
    setRenameErrorMessage("");

    try {
      const [error, response] = await parsePromise(
        renameQuestionnaire(questionnaireToRename.id, newName, questionnaireToRename.questionnaire_name)
      );

      if (error) {
        // Check for specific error message from API
        const errorMessage = error?.response?.data?.error_message;
        
        if (errorMessage && errorMessage.includes("already exists")) {
          setRenameErrorMessage("Name already exists. Please choose a different name to proceed.");
          return;
        }
        
        // Extract actual API error message for other errors
        let apiErrorMessage = error?.message || "An unexpected error occurred. Please try again later.";
        
        if (error?.response?.data?.error_message) {
          const errorData = error?.response?.data?.error_message;
          if (Array.isArray(errorData)) {
            apiErrorMessage = errorData[0] || apiErrorMessage;
          } else if (typeof errorData === 'string') {
            apiErrorMessage = errorData;
          }
        }
        
        setRenameErrorMessage(apiErrorMessage);
        return;
      }

      if (response?.success) {
        addNotification({
          type: "success",
          message: "Questionnaire & Document Set renamed successfully.",
        });

        await fetchQuestionnaires();
        setIsRenameModalOpen(false);
        setQuestionnaireToRename(null);
        setRenameErrorMessage("");
      }
    } catch (error) {
      console.error("Error renaming questionnaire:", error);
      setRenameErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameModalClose = () => {
    if (!isRenaming) {
      setIsRenameModalOpen(false);
      setQuestionnaireToRename(null);
      setRenameErrorMessage("");
    }
  };

  const handleEditQuestionnaire = (questionnaire: Questionnaire) => {
    openWizard(questionnaire.id);
  };

  const handleBackClick = () => {
    navigate(`/${ROUTES.SETTINGS}`, { state: { tab: 'third-party' } });
  };


  return (
    <div className="w-full h-full flex flex-col">
      {/* Header with Figma CSS specifications - Frame 5712 */}
      <div
        className="flex flex-col items-start gap-4 bg-white border-b border-[#E4E4E7]"
        style={{
          boxSizing: 'border-box',
          padding: '16px',
          width: '100%',
          height: '52px',
          isolation: 'isolate',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0
        }}
      >
        {/* Questions Header */}
        <div
          className="flex flex-row justify-between items-center"
          style={{
            boxSizing: 'border-box',
            padding: '0px',
            gap: '8px',
            isolation: 'isolate',
            width: '100%',
            height: '20px',
            flex: 'none',
            order: 0,
            alignSelf: 'stretch',
            flexGrow: 0,
            zIndex: 1
          }}
        >
          {/* Frame 2767 - Left section with back arrow and title */}
          <div
            className="flex flex-row items-center"
            style={{
              padding: '0px',
              gap: '8px',
              margin: '0 auto',
              width: '100%',
              height: '20px',
              flex: 'none',
              order: 0,
              flexGrow: 1,
              zIndex: 0
            }}
          >
            {/* Back Arrow */}
            <button
              onClick={handleBackClick}
              className="flex items-center justify-center"
              style={{
                width: '20px',
                height: '20px',
                flex: 'none',
                order: 0,
                flexGrow: 0
              }}
            >
              <ArrowLeft
                size={20}
                style={{
                  color: '#000000',
                  strokeWidth: '1.5px'
                }}
              />
            </button>

            {/* Title */}
            <h4
              style={{
                width: '100%',
                height: '20px',
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#171717',
                flex: 'none',
                order: 1,
                flexGrow: 1
              }}
            >
              Third-Party Risk Questionnaire & Document Sets
            </h4>
          </div>
        </div>
      </div>

      <div className="grow w-full overflow-hidden flex flex-col">
        <div className="grow overflow-hidden flex">
          <div className="flex-1 flex flex-col bg-white overflow-auto p-6">
            <ZTableDeprecrated<Questionnaire>
              className="border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto"
              loading={loading}
              rows={questionnaires}
              columns={[
                {
                  type: "string",
                  fieldName: "questionnaire_name",
                  headerName: "Questionnaire & Document Set",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-[#717179]" />
                        <div className="flex items-center gap-2">
                          <p className="text-[#333]">{row.questionnaire_name}</p>
                        </div>
                      </div>
                    );
                  },
                },
                {
                  type: "number",
                  fieldName: "number_of_questions",
                  headerName: "No. of Questions",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="text-[#333]">
                        {row.number_of_questions}
                      </div>
                    );
                  },
                },
                {
                  type: "string",
                  fieldName: "last_updated_by",
                  headerName: "Updated by",
                  sortable: true,
                  getSortValue: (row) => {
                    return getMemberName(row.last_updated_by || "");
                  },
                  renderCell: (row) => {
                    const userName = getMemberName(row.last_updated_by || "");

                    return (
                      <div className="flex items-center gap-2">
                        <ZAvatarGradient
                          name={userName}
                          size={20}
                          textSize={8}
                        />
                        <span className="text-[#333]">{userName}</span>
                      </div>
                    );
                  },
                },
                {
                  type: "string",
                  fieldName: "updated_on",
                  headerName: "Last Updated",
                  sortable: true,
                  renderCell: (row) => {
                    return (
                      <div className="text-[#333]">
                        {formatLastUpdated(row.last_updated_on)}
                      </div>
                    );
                  },
                },
                {
                  type: "custom",
                  fieldName: "actions",
                  headerName: "",
                  renderCell: (row) => {
                    return (
                      <div className="flex justify-end items-center h-10">
                        <Dropdown placement="bottom-end">
                          <DropdownTrigger>
                            <Button
                              isIconOnly
                              variant="light"
                              className="bg-transparent"
                              disabled={loading}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 10.8333C10.4603 10.8333 10.8334 10.4602 10.8334 10C10.8334 9.53976 10.4603 9.16667 10 9.16667C9.53978 9.16667 9.16669 9.53976 9.16669 10C9.16669 10.4602 9.53978 10.8333 10 10.8333Z"
                                  fill="#717179"
                                />
                                <path
                                  d="M10 5.83333C10.4603 5.83333 10.8334 5.46024 10.8334 5C10.8334 4.53976 10.4603 4.16667 10 4.16667C9.53978 4.16667 9.16669 4.53976 9.16669 5C9.16669 5.46024 9.53978 5.83333 10 5.83333Z"
                                  fill="#717179"
                                />
                                <path
                                  d="M10 15.8333C10.4603 15.8333 10.8334 15.4602 10.8334 15C10.8334 14.5398 10.4603 14.1667 10 14.1667C9.53978 14.1667 9.16669 14.5398 9.16669 15C9.16669 15.4602 9.53978 15.8333 10 15.8333Z"
                                  fill="#717179"
                                />
                              </svg>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Questionnaire Actions"
                            variant="flat"
                            onAction={(key) => {
                              if (key === "edit") {
                                handleEditQuestionnaire(row);
                              } else if (key === "delete") {
                                handleDeleteClick(row);
                              } else if (key === "rename") {
                                handleRenameClick(row);
                              }
                            }}
                          >
                            <DropdownItem key="edit" startContent={<Edit size={16} />}>
                              Edit
                            </DropdownItem>
                            <DropdownItem key="rename" startContent={<TextCursorInput size={16} />}>
                              Rename
                            </DropdownItem>
                            <DropdownItem key="delete" color="danger" startContent={<Trash2 size={16} />} className="text-danger">
                              Remove
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    );
                  },
                  className: "w-[60px]",
                },
              ]}
              isHeaderSticky={true}
              isCompact={true}
              removeWrapper={true}
              getRowId={(row) => row.id}
              tableHeaderProps={{
                className: "shadow-none rounded-none",
              }}
              tableBodyProps={{
                emptyContent: loading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner
                      containerClassName="bg-transparent relative"
                      className="mx-auto"
                    />
                  </div>
                ) : (
                  "No questionnaires found"
                ),
              }}
              tableRowProps={(row, rowIndex) => ({
                className: `${rowIndex === 0 ? "border-none" : "border-y"} hover:bg-[#F2F2F2] cursor-pointer`,
                onClick: () => {
                  handleEditQuestionnaire(row);
                },
              })}
              classNames={{
                wrapper: "shadow-none rounded-none",
              }}
              aria-label="Third-Party Risk Assessment Questionnaires"
            />
          </div>
        </div>
      </div>


      <CreateQuestionnaireModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
      />

      <QuestionnaireRenameModal
        isOpen={isRenameModalOpen}
        onClose={handleRenameModalClose}
        onRename={handleRenameConfirm}
        questionnaire={questionnaireToRename}
        isRenaming={isRenaming}
        errorMessage={renameErrorMessage}
      />

      <QuestionnaireConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        questionnaire={questionnaireToDelete}
        isLoading={isDeleting}
      />

    </div>
  );
};

export default QuestionnairesList;